import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";

const STATUSES = ["New", "Contacted", "Appointment", "Quote Sent", "Won", "Lost"];

export default function Leads() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("");
  const { data } = useQuery({
    queryKey: ["cms-leads", filter],
    queryFn: async () => (await api.get(`/cms/leads${filter ? `?status=${encodeURIComponent(filter)}` : ""}`)).data,
  });
  const leads = data?.leads || [];

  const update = async (id, patch) => {
    try {
      await api.patch(`/cms/leads/${id}`, patch);
      qc.invalidateQueries({ queryKey: ["cms-leads"] });
      toast.success("Lead updated");
    } catch { toast.error("Update failed"); }
  };

  const input = "bg-obsidian border border-white/15 rounded-lg px-3 py-1.5 text-xs text-ink focus:border-copper";

  return (
    <div>
      <h1 className="text-3xl font-bold uppercase tracking-tight text-ink mb-1">Leads</h1>
      <p className="text-subtle text-sm mb-6">Enquiries from the website (with UTM & source tracking)</p>

      <div className="flex gap-2 mb-5 flex-wrap" data-testid="lead-filters">
        <button onClick={() => setFilter("")} className={`text-xs px-3 py-1.5 rounded-full border ${filter === "" ? "border-copper text-copper" : "border-white/15 text-subtle"}`}>All</button>
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s.replace(/\s/g, "-")}`} className={`text-xs px-3 py-1.5 rounded-full border ${filter === s ? "border-copper text-copper" : "border-white/15 text-subtle"}`}>{s}</button>
        ))}
      </div>

      <div className="space-y-3">
        {leads.length === 0 && <p className="text-faint text-sm">No leads yet.</p>}
        {leads.map((l) => (
          <div key={l.id} className="card-surface !hover:translate-y-0 p-5" data-testid={`lead-${l.id}`}>
            <div className="flex justify-between items-start flex-wrap gap-3">
              <div>
                <p className="text-ink font-semibold">{l.name} · <span className="text-subtle font-normal">{l.phone}</span></p>
                <p className="text-xs text-faint mt-1">
                  {[l.vehicle_brand, l.vehicle_model, l.vehicle_year].filter(Boolean).join(" ")}
                  {l.service && ` · ${l.service}`}{l.branch && ` · ${l.branch}`} · {l.language?.toUpperCase()}
                </p>
                {l.message && <p className="text-sm text-subtle mt-2">{l.message}</p>}
                <p className="text-[11px] text-faint mt-2">{new Date(l.created_at).toLocaleString()} · src: {l.source_page || "-"} {l.utm_source && `· utm:${l.utm_source}`}</p>
              </div>
              <div className="flex flex-col gap-2 items-end">
                <select data-testid={`lead-status-${l.id}`} value={l.status} onChange={(e) => update(l.id, { status: e.target.value })} className={input}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <input
              data-testid={`lead-notes-${l.id}`}
              defaultValue={l.admin_notes}
              placeholder="Admin notes…"
              onBlur={(e) => e.target.value !== l.admin_notes && update(l.id, { admin_notes: e.target.value })}
              className={`${input} w-full mt-3`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
